import type { HarvestPayload, TaxonLite } from "@/types";
import { formatWeightKg } from "@/utils";

// A single recorded picking. Weights are always in kilograms (the API normalizes
// the entered g/kg to `quantity_kg`). See HarvestPayload.
export default class Harvest {
  id: number;
  crop: number;
  variety: number | null;
  varietyName: string | null;
  varietyImage: string | null;
  taxon: TaxonLite;
  date: Date;
  quantityKg: number;
  note: string;
  createdBy: number | null;
  createdAt: Date;
  // Value of this picking in euros (read-only, null when the taxon has no reference price).
  marketPrice: number | null; // retail amount → savings
  producerPrice: number | null; // producer amount → revenue

  constructor({
    id,
    crop,
    variety,
    variety_name: varietyName,
    variety_image: varietyImage,
    taxon,
    date,
    quantity_kg: quantityKg,
    note,
    created_by: createdBy,
    created_at: createdAt,
    market_price: marketPrice,
    producer_price: producerPrice,
  }: HarvestPayload) {
    this.id = id;
    this.crop = crop;
    this.variety = variety;
    this.varietyName = varietyName;
    this.varietyImage = varietyImage;
    this.taxon = taxon;
    this.date = new Date(Date.parse(date));
    this.quantityKg = quantityKg;
    this.note = note;
    this.createdBy = createdBy;
    this.createdAt = new Date(Date.parse(createdAt));
    this.marketPrice = marketPrice;
    this.producerPrice = producerPrice;
  }

  // Weight in grams, for display when the picking is small.
  get quantityG(): number {
    return this.quantityKg * 1000;
  }

  // Picked weight with the handiest unit ("1,2 kg" / "800 g").
  get quantityDisplay(): string {
    return formatWeightKg(this.quantityKg);
  }

  // Retail value of this picking ("7,15 €"), i.e. the home grower's savings. Null when the
  // taxon has no reference price (then show nothing, never "0 €").
  get marketValueDisplay(): string | null {
    if (this.marketPrice == null) return null;
    return `${this.marketPrice.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  }
}
